import React, { FC } from 'react'
import { useEffectOnce } from 'react-use'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { FullPageOverlay } from 'apps/fleet-planner/components/modals/full-page-overlay/FullPageOverlay'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics/useFMSAnalytics'
import { getFormattedPercentage } from 'apps/tms/routing-guide/utils/formatPercentage'
import { CheckFilledIcon, CloseIcon } from 'transfix-icons'

const useStatsBoxModal = makeStyles(theme => ({
  container: {
    padding: '0px 8px 20px',
  },
  shipmentLine: {
    marginTop: 16,
    display: 'flex',
    flexDirection: 'row',
    gap: 8,

    '& svg': {
      width: '20px',
      height: '20px',

      '&.check': {
        color: brandColors.green2,
      },

      '&.close': {
        padding: theme.spacing(0.5),
        color: brandColors.coolGray5,
        backgroundColor: brandColors.coolGray2,
        borderRadius: '50px',
      },
    },
  },
  content: {
    marginTop: 10,
    marginBottom: 30,
    color: brandColors.coolGray5,
    flexDirection: 'column',
    display: 'flex',
    gap: 10,
  },
  rateContent: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    color: brandColors.black,
    alignItems: 'center',
  },
  close: {
    position: 'absolute',
    cursor: 'pointer',
    right: 24,
  },
  line: {
    border: `1px solid ${brandColors.coolGray2}`,
  },
  badged: {
    borderRadius: 44,
    fontWeight: 700,
    padding: '4px 12px',
    height: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    '&.doingGreat': {
      background: brandColors.green1,
      color: brandColors.green3,
    },

    '&.almostThere, &.needsImprovement': {
      background: brandColors.coolGray3,
      color: brandColors.coolGray8,
    },

    [theme.breakpoints.down('lg')]: {
      fontSize: 12,
    },
  },
}))

interface ITypeContent {
  label: string
  target?: string
  content?: string
  badged?: string
  successContent?: string
  failureContent?: string
}

interface IRateContent {
  [variant: string]: ITypeContent
}

const rateType: IRateContent = {
  onTimeRate: {
    label: 'On time rate',
    content:
      'of your most recent 100 stops on Transfix loads arrived within 30 minutes of pickup and delivery time.',
    target: '95%',
    successContent: 'stops picked up and dropped off within the 30 minute appointment window.',
    failureContent: 'stops picked up or dropped off outside the 30 minute appointment window.',
  },
  trackingQuality: {
    label: 'Tracking quality',
    content: 'of your most recent 50 Transfix loads tracked from an ELD or Transfix Mobile App.',
    target: '95%',
    successContent: 'loads tracked with an ELD or Transfix Mobile App.',
    failureContent: 'loads did not track with an ELD or Transfix Mobile App',
  },
  lateCancellation: {
    label: 'Late cancellation',
    content:
      'of your most recent 50 Transfix loads had a cancellation within 24 hours of the appointment time.',
    target: '<5%',
    successContent:
      'loads didn’t cancel or cancelled more than 24 hours in advance of the pickup appointment time.',
    failureContent: 'loads cancelled less than 24 hours before the pickup appointment time.',
  },
}

interface IMetricBadgedType {
  [type: string]: string
}

const metricBadged: IMetricBadgedType = {
  doingGreat: 'Doing great!',
  almostThere: 'Almost there!',
  needsImprovement: 'Keep going!',
}

interface IStatsBoxOverlayProps {
  rate: number | undefined
  failure: number | null
  success: number | null
  open: boolean
  onClose: () => void
  variant: string
  metric: string | null
}

export const StatsBoxOverlay: FC<IStatsBoxOverlayProps> = ({
  failure = 0,
  metric,
  onClose,
  open = false,
  rate,
  success = 0,
  variant = '',
}) => {
  const classes = useStatsBoxModal()
  const { isMobile } = useBreakpointContext()
  const { trackEvent } = useFMSAnalytics()

  useEffectOnce(() => {
    if (variant === 'onTimeRate') {
      trackEvent('Carrier Scorecard Page', 'RATE_ON_TIME_RATE')
    }

    if (variant === 'trackingQuality') {
      trackEvent('Carrier Scorecard Page', 'RATE_TRACKING_QUALITY')
    }

    if (variant === 'lateCancellation') {
      trackEvent('Carrier Scorecard Page', 'RATE_LATE_CANCELLATION')
    }
  })

  return (
    <FullPageOverlay
      title={
        <div>
          <Typography variant='h2' component='p'>
            {rateType[variant]?.label}
          </Typography>
        </div>
      }
      fullScreen={isMobile}
      showContentDividers={isMobile}
      maxWidth='md'
      isOpen={open}
      onClose={onClose}>
      <div className={classes.container}>
        <div className={classes.content}>
          <div className={classes.rateContent}>
            <Typography variant='h2' component='p'>
              {getFormattedPercentage(rate || '0')}
            </Typography>
            {metric && <div className={`${classes.badged} ${metric}`}>{metricBadged[metric]}</div>}
          </div>

          <Typography variant='body1' component='p'>
            {rateType[variant]?.content}
          </Typography>

          <Typography variant='body2' component='p'>
            Target: {rateType[variant]?.target}
          </Typography>
        </div>

        <Typography variant='subtitle1' component='strong'>
          Shipment breakdown
        </Typography>

        <div className={classes.shipmentLine}>
          <CheckFilledIcon className='check' />
          <Typography variant='body1' component='strong'>
            {success} {rateType[variant]?.successContent}
          </Typography>
        </div>

        <hr />

        <div className={classes.shipmentLine}>
          <CloseIcon className='close' />
          <Typography variant='body1' component='strong'>
            {failure} {rateType[variant]?.failureContent}
          </Typography>
        </div>
      </div>
    </FullPageOverlay>
  )
}
